<template>
  <div>
    <div class="dashboard__view mt__8">
      <div class="list">
        <div class="tab__content">
          <div class="loader" v-if="loading">
            <span aria-hidden="true" class="spinner-border" />
          </div>

          <form @submit.prevent="toggleShowOverlay()" v-else>
            <div class="profile">
              <div class="profile__left">
                <div class="profile__title">Initiate Transfer</div>
                <div class="profile__caption">
                  Be sure to enter the right receipient account information
                </div>
              </div>
              <div class="profile__right">
                <div class="form__flex">
                  <div class="form__flex__item">
                    <label class="form__label" for>From</label>

                    <SelectSearch
                      :options="
                        collectionAccounts.map(({ text, value, ...data }) => ({
                          value,
                          text,
                          data,
                        }))
                      "
                      :selected-option="{
                        value: payoutForm.sourceAccount,
                        text: payoutForm.sourceAccountName,
                      }"
                      :on-option-selected="
                        (value) => selectReservedAccount(value)
                      "
                      placeholder="Select Collection Account"
                    />

                    <span
                      class="attention"
                      v-if="$v.payoutForm.sourceAccountNumber.$error"
                      >Please select a collection account to payout from</span
                    >
                  </div>
                </div>

                <div class="form__flex">
                  <div class="form__flex__item">
                    <label class="form__label" for>Amount</label>
                    <input
                      class="form__input"
                      type="text"
                      v-model.trim="$v.payoutForm.amount.$model"
                      v-money="mask"
                    />
                    <span
                      class="attention"
                      v-if="$v.payoutForm.amount.$error && submitAttempted"
                      >Please specify an amount</span
                    >
                  </div>
                </div>

                <div class="form__flex">
                  <div class="form__flex__item">
                    <label class="form__label form__label--2" for>
                      <span>Narration</span>
                    </label>
                    <text-area
                      label="Add a note"
                      class="form__input"
                      :handle-text-area="(val) => (payoutForm.narration = val)"
                      :required="false"
                    />
                  </div>
                </div>

                <!--Beneficiary Section-->
                <div class="ml-3 d-flex justify-content-between mb-3">
                  <h1 class="profile__title mb-0">Beneficiary Details</h1>
                </div>

                <div class="form__flex">
                  <div
                    class="form__flex__item"
                    :class="{ 'bg-disabled': useSavedBeneficiaries }"
                  >
                    <label class="form__label" for>Bank</label>

                    <SelectSearch
                      :options="
                        bankList.map(({ id, value }) => ({
                          value: id,
                          text: value,
                        }))
                      "
                      :selected-option="{
                        value: payoutForm.beneficiaryBank,
                        text: getBankName(payoutForm.beneficiaryBank),
                      }"
                      :on-option-selected="
                        ({ value }) =>
                          ($v.payoutForm.beneficiaryBank.$model = value)
                      "
                      :placeholder="
                        useSavedBeneficiaries && payoutForm.beneficiaryBank
                          ? getBankName(payoutForm.beneficiaryBank)
                          : 'Select Bank'
                      "
                      :disabled="useSavedBeneficiaries"
                    />

                    <span
                      class="attention"
                      v-if="$v.payoutForm.beneficiaryBank.$error"
                      >Please select the recipient's bank</span
                    >
                  </div>
                </div>

                <div class="form__flex">
                  <div
                    class="form__flex__item"
                    :class="{ 'bg-disabled': useSavedBeneficiaries }"
                  >
                    <label class="form__label" for
                      >Beneficiary Account Number</label
                    >

                    <div class="d-flex">
                      <input
                        @keydown="preventKeys"
                        @input="nameEnquiry()"
                        class="form__input"
                        maxlength="11"
                        type="text"
                        :readonly="useSavedBeneficiaries"
                        v-model.trim="$v.payoutForm.beneficiaryAccount.$model"
                      />

                      <div class="mx-2" v-if="runningEnquiry">
                        <span aria-hidden="true" class="spinner-border" />
                      </div>
                    </div>

                    <span
                      class="attention"
                      v-if="$v.payoutForm.beneficiaryAccount.$error"
                      >Please specify the recipient's bank</span
                    >
                  </div>
                </div>

                <div class="form__flex">
                  <div class="form__flex__item mb-3 bg-disabled">
                    <label class="form__label" for
                      >Beneficiary Account Name</label
                    >
                    <input
                      class="form__input"
                      type="text"
                      readonly
                      v-model.trim="$v.payoutForm.beneficiaryName.$model"
                    />
                    <span
                      class="attention"
                      v-if="$v.payoutForm.beneficiaryName.$error"
                      >Name resolution failed, please check the account
                      number.</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="action">
              <button
                :disabled="loading || runningEnquiry || submitting"
                type="submit"
                class="btn btn--primary"
              >
                {{
                  loading || runningEnquiry || submitting
                    ? "Processing..."
                    : "Initiate Transfer"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <Overlay
      v-if="showOverlay"
      @completed="handleCompleted"
      @cancel="toggleShowOverlay"
    />
  </div>
</template>

<script>
import { formatAmount, formatMoneyMask } from "../../../lib/formatAmount";
import { required, requiredIf, minLength } from "vuelidate/lib/validators";
import moment from "moment";
import Overlay from "./Overlay.vue";

import {
  reactive,
  onMounted,
  computed,
  watch,
  toRefs,
  getCurrentInstance,
} from "@vue/composition-api";
import TextArea from "../../../components/textarea";
import SelectSearch from "../../../components/selectsearch";
import EmptyState from "../../../components/empty-state";

export default {
  components: {
    EmptyState,
    SelectSearch,
    TextArea,
    Overlay,
  },
  metaInfo: {
    title: "Payout - Single",
  },

  validations: {
    payoutForm: {
      sourceAccountNumber: {
        required,
      },
      amount: {
        notEmpty: (value) => value !== "₦ 0.00",
      },
      beneficiaryBank: {
        required,
      },
      beneficiaryName: {
        required,
      },
      beneficiaryAccount: {
        required,
      },
    },

    payoutPin: {
      required,
      minLength: minLength(4),
    },
  },

  setup(props, { root }) {
    const store = root.$store;
    const vueInstance = getCurrentInstance().proxy;

    const mask = {
      decimal: ".",
      thousands: ",",
      prefix: "₦ ",
      suffix: "",
      precision: 2,
      masked: false /* doesn't work with directive */,
    };

    // data
    const data = reactive({
      payoutForm: {
        sourceAccountNumber: "",
        sourceAccountBank: "",
        sourceAccountName: "",
        amount: "₦ 0.00",
        narration: "",
        beneficiaryBank: "",
        beneficiaryName: "",
        beneficiaryAccount: "",
      },
      payoutPin: "",
      beneficiaries: [],
      collectionAccounts: [],
      // the payout command can be 'initiate' for single payout and 'scheduled' scheduled payouts.
      payoutCommand: "initiate",
      failureMessage: null,
      showModal: false,
      useSavedBeneficiaries: false,
      submitAttempted: false,
      scheduleMode: false,
      showPinModal: false,
      submitting: false,
      runningEnquiry: false,
      loading: false,
      payoutSuccessful: false,
      payoutFailed: false,
      showOverlay: false,
      authPin: null,
    });

    //mounted
    onMounted(async () => {
      data.loading = true;
      await fetchCollectionAccounts();
      data.loading = false;
    });

    //watchers
    watch(
      () => root.$route.fullPath,
      () => {}
    );

    const userDetails = computed(() => store.getters.getUserDetails);
    const bankList = computed(() => store.getters.getBankListKeyAndValuePair);

    //methods

    const nameEnquiry = async () => {
      try {
        if (
          data.payoutForm.beneficiaryBank &&
          data.payoutForm.beneficiaryAccount.length >= 10
        ) {
          data.runningEnquiry = true;
          const nuban = data.payoutForm.beneficiaryAccount;
          const bank_code = data.payoutForm.beneficiaryBank;
          const res = await store.dispatch("doBankEnquiry", {
            nuban,
            bank_code,
          });
          data.payoutForm.beneficiaryName = res.body.data.account_name;
          data.runningEnquiry = false;
        }
      } catch (e) {
        data.runningEnquiry = false;
        store.dispatch("notify", {
          e,
          str: "Could not resolve account. Please check the account number",
        });
      }
    };

    const toggleShowOverlay = () => {
      if (data.showOverlay) {
        data.showOverlay = false;
        return;
      }
      vueInstance.$v.payoutForm.$touch();
      if (vueInstance.$v.payoutForm.$invalid) {
        return;
      }
      data.showOverlay = !data.showOverlay;
    };

    const handleCompleted = (e) => {
      data.authPin = e;
      if (data.authPin) {
        submit();
      }
    };

    const toggleScheduleMode = () => {
      data.scheduleMode = !data.scheduleMode;
    };

    const getBankName = (code) => {
      return store.getters.getBankName(code);
    };

    const fetchCollectionAccounts = async () => {
      const { accounts } = await store.dispatch("getCollectionAccounts");
      data.collectionAccounts = accounts.map((account) => {
        return {
          value: account,
          text: `${account.accountNumber} - ${account.bankName}`,
          ...account,
        };
      });
    };

    const selectReservedAccount = (account) => {
      data.payoutForm.sourceAccountName = account.data.accountName;
      data.payoutForm.sourceAccountNumber = account.data.accountNumber;
      data.payoutForm.sourceAccountBank = account.data.bankCode;
    };

    const clearBeneficiaryForm = () => {
      data.payoutForm = {
        sourceAccountNumber: "",
        sourceAccountBank: "",
        sourceAccountName: "",
        amount: "₦ 0.00",
        narration: "",
        beneficiaryBank: "",
        beneficiaryName: "",
        beneficiaryAccount: "",
      };
      vueInstance.$v.payoutForm.$reset();
    };

    const submit = async () => {
      data.showOverlay = false;
      vueInstance.$v.payoutForm.$touch();
      if (vueInstance.$v.payoutForm.$invalid) {
        return;
      }

      data.submitting = true;

      let payload = {
        source: data.payoutForm.sourceAccountNumber,
        source_bank: data.payoutForm.sourceAccountBank,
        destination: data.payoutForm.beneficiaryAccount,
        destination_bank: data.payoutForm.beneficiaryBank,
        amount: Number(formatMoneyMask(data.payoutForm.amount, false)),
        narration: data.payoutForm.narration,
        totp_code: data.authPin,
      };

      const response = await store.dispatch(
        "submitTransferApprovalRequest",
        payload
      );

      data.submitting = false;
      if (response) {
        store.dispatch("notify", {
          s: {},
          str: "Transfer request success.",
        });
        this.$router.go(-1);
      }
    };

    return {
      ...toRefs(data),
      nameEnquiry,
      userDetails,
      selectReservedAccount,
      moment,
      getBankName,
      bankList,
      formatAmount,
      submit,
      mask,
      handleCompleted,
      toggleShowOverlay,
    };
  },
};
</script>

<style lang="scss">
.content-window {
  width: fit-content;
  border: none !important;
  min-width: 100%;
}

.profile {
  border-top: none !important;

  &__title {
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 27px;
    color: #7a153c;
  }
}

.tab__item--nav {
  border: none !important;
}
.tab__item--nav.router-link-exact-active.router-link-active {
  padding-left: 0;
  height: 39px;
  padding-right: 0;
  border-bottom: 1px solid #000 !important;
}
</style>

<style lang="scss">
.text-area {
  border: none !important;
  resize: none;
  margin-bottom: 0.8rem;
  height: 30px !important;
}

.input-date {
  position: relative;
  .form__item {
    margin-bottom: 0;
  }
}

.form__flex__dropdown {
  .drop-down-component {
    margin-bottom: 0;

    .drop-down {
      width: 100%;
      padding-top: 2rem;
      padding-bottom: 2.5rem;
    }

    .dropdown-options {
      overflow-y: auto;
      height: 100%;
      min-height: 12rem;
    }
  }
}

.sudo-modal {
  min-width: 50% !important;
  display: block !important;
  padding-bottom: 0 !important;

  &__content {
    display: block !important;
    position: relative;

    .form__flex__item {
      background: #ebf0f4;
      text-align: left;
    }

    .dropdown-options {
      overflow-y: auto;
      height: 100%;
      min-height: 12rem;
    }
  }

  .modal-body {
    justify-content: center !important;

    h2 {
      font-weight: 500;
      font-size: 32px;
      line-height: 38px;
      margin-bottom: 14px;
    }

    p {
      margin-bottom: 31px;
      color: #616e7c;
    }

    svg {
      margin-bottom: 12px;
    }

    textarea {
      resize: none;
      height: 4rem;
    }

    .boxed {
      width: 60%;
      margin: 0.5rem auto 1.5rem;

      .form__flex__item,
      button {
        margin-left: 0;
        width: 100%;
      }

      .details__card__item {
        padding: 0 0 24px;
        border: none;
      }
    }
  }
}
//for the datepicker
.w-100 {
  div input {
    min-width: 100% !important;
  }
}
</style>

<style lang="scss" scoped>
.mt__8 {
  margin-top: 8%;
}

.bg-disabled {
  background: #ebf0f4;
}

.w-48 {
  width: 48%;
}

.form__flex__item {
  .input-time {
    padding-bottom: 7px;
  }
}

.form__flex__dropdown {
  margin-left: 16px;
  margin-bottom: 32px;
  width: 100%;
  position: relative;
}

.toggle {
  display: flex;
  align-items: center;
  // background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), #261940;
  border-radius: 100px;
  // padding: 12px 18px;
  &.toggle-danger {
    .toggleWrapper {
      background: #e4ebf6 !important;
    }

    .toggle__text {
      color: #000000 !important;
    }
  }

  .live {
    background: #e90c0c !important;
  }

  .toggleWrapper {
    background: red;
    border-radius: 20px;
    padding: 1px;
    height: 20px;
    width: 35px;
  }

  &__text {
    color: #e90c0c;
    margin-left: 5px;
    font-size: 14px;
  }

  &__display {
    font-size: 12px;
    font-weight: 600;
    margin-right: 8px;
  }
}

.beforeAnimation {
  transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
}

.afterAnimation {
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 0px 0 hsla(0, 0%, 0%, 0.04),
    0 4px 9px hsla(0, 0%, 0%, 0.13), 0 3px 3px hsla(0, 0%, 0%, 0.05);
  transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
}

.toggleWrapper input.mobileToggle {
  opacity: 0;
  position: absolute;
}

.toggleWrapper input.mobileToggle + label {
  position: relative;
  display: inline-block;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: 0.4s ease;
  width: 30px;
  height: 15px;
  border-radius: 60px;
}

.toggleWrapper input.mobileToggle + label:before {
  content: "";
  position: absolute;
  display: block;
  transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
  height: 18px;
  width: 32px;
  top: 0;
  left: 0;
  border-radius: 30px;
  background-color: rgba(#ffffff, 0.2);
  cursor: pointer;
}

.toggleWrapper input.mobileToggle + label:after {
  content: "";
  position: absolute;
  display: block;
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 0px 0 hsla(0, 0%, 0%, 0.04),
    0 4px 9px hsla(0, 0%, 0%, 0.13), 0 3px 3px hsla(0, 0%, 0%, 0.05);
  transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
  background: #ffffff;
  box-shadow: 0px 2px 2px rgba(45, 45, 45, 0.2);
  height: 18px;
  width: 18px;
  top: -0px;
  left: -2px;
  border-radius: 60px;
  cursor: pointer;
}

.toggleWrapper input.mobileToggle:checked + label:before {
  // background: #67d495;
  transition: width 0.2s cubic-bezier(0, 0, 0, 0.1);
}

.toggleWrapper input.mobileToggle:checked + label:after {
  left: 16px;
}
</style>
